/**
 * OrderData
 * --
 */
import React, { useEffect, useState } from 'react';
import { Content, Title, ModalLayout } from 'components';
import {
  Button,
  Input,
  Select,
  Upload,
  Spin,
  Tag,
  Table,
  Space,
  Divider,
  Tabs,
  Descriptions,
  Empty,
  Flex,
  Radio,
  DatePicker,
  message,
} from 'antd';
import MessageAlert, {
  stringToMoneyFormat,
  isNull,
  getPaymentDueDate,
} from 'utils';
import trackDelivery from 'utils/TrackDelivery';
import moment from 'moment';
import dayjs from 'dayjs';
import {
  FormOutlined,
  UploadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import DeliveryCompany from 'constants/DeliveryCompany';
import RegularDetail from './RegularDetail';
import * as XLSX from 'xlsx/xlsx.mjs';
import {
  PAYMENT_METHOD_CONSTANT, // 주문 유형
  DELIVERY_STATUS,
  PAYMENT_STATUS_CONSTANT,
} from 'constants/ProductConstants';
import { PURCHASE_STATUS } from 'constants/OrderConstants';
import {
  useUpdateParcelServiceStatusBulk,
  useBulkUpdateParcelService,
} from 'hooks/ParcelServiceHooks';
const initialRegisterDeliveryInfo = {
  delivery_company: '',
  invoice_number: '',
};

/**----------------------------------------
 * [Component] 필터
 * ----------------------------------------
 */
const RegularData = ({
  type,
  orderList,
  orderAllList,
  onSaveDeliveryInfo,
  onReload,
  onCancel,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== PROPS ===== */
  /* ===== MUTATE ===== */
  const { updateParcelServiceStatusBulk } = useUpdateParcelServiceStatusBulk();
  const { bulkUpdateParcelService } = useBulkUpdateParcelService();
  /* ===== STATE ===== */
  // 로딩
  const [loading, setLoading] = useState(false);
  // 선택항목
  const [selectedList, setSelectedList] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  // 상세보기 모달
  const [detailModal, setDetailModal] = useState(null);
  const [tavType, setTabType] = useState('SUBSCRIPTION');
  // 택배정보 등록 모달
  const [registerDeliveryInfoModal, setRegisterDeliveryInfoModal] =
    useState(null);
  // 택배정보 입력 데이터
  const [registerDeliveryInfo, setRegisterDeliveryInfo] = useState(
    initialRegisterDeliveryInfo
  );
  // 택배정보 입력 데이터
  const [currentDeliveryCompany, setCurrentDeliveryCompany] = useState(1);
  // 탭
  const [currentTab, setCurrentTab] = useState(1);
  // 택배정보 입력 데이터
  const [uploadData, setUploadData] = useState(null);

  const [dateModal, setDateModal] = useState(null);
  const searchInput = React.useRef(null);

  /* ===== VARIABLE ===== */
  const handleTableSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };
  const handleTableReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`검색어를 입력해주세요`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleTableSearch(selectedKeys, confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleTableSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            검색
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleTableReset(clearFilters, confirm)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            초기화
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) => {
      const [main, sub, third] = dataIndex?.split('.');
      const dataValue = third
        ? record[main][sub][third]
        : sub
        ? record[main][sub]
        : record[main];
      return dataValue
        ?.toString()
        ?.toLowerCase()
        ?.includes(value.toLowerCase());
    },
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) => text,
  });

  // 택배사 정보
  const deliveryCompanyList = Object.keys(DeliveryCompany).slice(0, 20);
  const columns = [
    {
      title: (
        <>
          주문일시
          <br />
          (신청일시)
        </>
      ),
      key: 'created_at',
      dataIndex: 'created_at',
      width: 160,
      align: 'center',
      render: (val) => moment(val * 1000).format('YYYY-MM-DD(HH:mm:ss)'),
      sorter: (a, b) => a.created_at - b.created_at,
    },
    {
      title: '주문번호',
      key: 'shop_order_id',
      dataIndex: 'shop_order_id',
      align: 'center',
      width: 100,
      sorter: (a, b) => a.shop_order_id - b.shop_order_id,
    },
    {
      title: '품목별주문번호',
      key: 'parcel_service_id',
      dataIndex: 'parcel_service_id',
      align: 'center',
      width: 120,
      sorter: (a, b) => a.parcel_service_id - b.parcel_service_id,
    },
    {
      title: '진행회차',
      key: 'regular_count',
      dataIndex: 'regular_count',
      width: 110,
      align: 'center',
      render: (_, record) => `${record?.regular_payment_info?.length ?? 0}회차`,
      sorter: (a, b) =>
        a.regular_payment_info.length - b.regular_payment_info.length,
    },
    {
      title: '상품명',
      key: 'product',
      dataIndex: 'product',
      width: 180,
      align: 'center',
      ...getColumnSearchProps('product.product_name'),
      render: (val) => val.product_name,
    },
    {
      title: '옵션정보',
      key: 'product',
      dataIndex: 'product',
      width: 180,
      align: 'center',
      render: (val) => (
        <ul style={{ fontSize: '0.9em' }}>
          {val?.product_option?.length
            ? val?.product_option?.map((item) => (
                <li>
                  - {item.option_title} /{' '}
                  {item?.option?.map((option) => (
                    <>{option?.option_name || '-'}</>
                  ))}
                </li>
              ))
            : '-'}
        </ul>
      ),
    },
    {
      title: '수량',
      key: 'num',
      dataIndex: 'num',
      width: 80,
      align: 'center',
      render: (val) => `${val ?? 0} 개`,
      sorter: (a, b) => a.num - b.num,
    },
    {
      title: '판매자',
      key: 'shop_information',
      dataIndex: 'shop_information',
      width: 170,
      align: 'center',
      ...getColumnSearchProps('shop_information.shop_information_name'),
      render: (record) => record?.shop_information_name ?? '',
    },
    {
      title: '구매자',
      key: 'user',
      dataIndex: 'user',
      width: 110,
      align: 'center',
      ...getColumnSearchProps('user.user_name'),
      render: (val) => val?.user_name ?? '',
    },
    {
      title: '주기',
      key: 'regular_cycle',
      dataIndex: 'regular_cycle',
      width: 110,
      align: 'center',
      render: (regular_cycle) => {
        const [count, type] = regular_cycle?.split('-') || ['-', 'D'];
        if (type === 'D') {
          return `${count}일 주기`;
        } else if (type === 'W') {
          return `${count}주 주기`;
        } else if (type === 'M') {
          return `${count}개월 주기`;
        }
      },
    },
    {
      title: '배송시작일',
      key: 'delivery_date',
      dataIndex: 'delivery_date',
      align: 'center',
      width: 150,
      render: (v, record) => (
        <Button
          onClick={() => {
            setDateModal({
              parcel_service_id: record.parcel_service_id,
              title: '배송시작일',
              key: 'delivery_date',
              dateValue: v,
            });
          }}
        >
          {dayjs.unix(v).format('YYYY-MM-DD')}
        </Button>
      ),
      sorter: (a, b) => a.delivery_date - b.delivery_date,
    },
    {
      title: '다음결제일',
      key: 'regular_updated_at',
      dataIndex: 'regular_updated_at',
      align: 'center',
      width: 150,
      render: (v, record) => (
        <Button
          onClick={() => {
            setDateModal({
              parcel_service_id: record.parcel_service_id,
              title: '다음결제일',
              key: 'regular_updated_at',
              dateValue: v,
            });
          }}
        >
          {dayjs.unix(v).format('YYYY-MM-DD')}
        </Button>
      ),
      sorter: (a, b) => a.regular_updated_at - b.regular_updated_at,
    },
    {
      title: '진행상태',
      key: 'is_active',
      dataIndex: 'is_active',
      align: 'center',
      width: 100,
      render: (val, record) => (
        <Select
          style={{ width: 90 }}
          options={[
            { value: 'Y', label: '진행중' },
            { value: 'N', label: '종료' },
          ]}
          onChange={(e) => {
            handleBulkUpdateParcelService(
              false,
              {
                parcel_service_id: record.parcel_service_id,
                is_active: e,
              },
              'is_active'
            );
          }}
          defaultValue={val}
        />
      ),
      filters: [
        { text: '진행중', value: 'Y' },
        { text: '종료', value: 'N' },
      ],
      onFilter: (val, record) => record.is_active === val,
    },
    {
      title: '미루기여부',
      key: 'is_regular_postponed',
      dataIndex: 'is_regular_postponed',
      align: 'center',
      width: 100,
      render: (val) => (val === 'Y' ? '미루기' : '미루지 않음'),
      filters: [
        { text: '미루기', value: 'Y' },
        { text: '미루지 않음', value: 'N' },
      ],
      onFilter: (val, record) => record.is_regular_postponed === val,
    },
    {
      title: '미루기횟수',
      key: 'regular_cycle',
      dataIndex: 'regular_cycle',
      align: 'center',
      width: 100,
      render: (val) => `${val.split('-')?.[2] ?? 0} 회`,
      sorter: (a, b) =>
        a?.regular_cycle?.split('-')?.[2] ??
        0 - b?.regular_cycle?.split('-')?.[2] ??
        0,
    },
    {
      title: '최대미루기횟수',
      key: 'max_regular_delivery_count',
      dataIndex: 'max_regular_delivery_count',
      align: 'center',
      width: 130,
      render: (val) => `${val} 회`,
      sorter: (a, b) =>
        a.max_regular_delivery_count - b.max_regular_delivery_count,
    },
    {
      title: '#',
      align: 'center',
      width: 70,
      fixed: 'right',
      render: (row, record) => (
        <>
          <Button
            block
            size="small"
            onClick={() =>
              handleBulkUpdateParcelService(
                false,
                {
                  ...row,
                  key: '미루기',
                },
                'is_regular_postponed'
              )
            }
          >
            미루기처리
          </Button>
          <Button
            block
            size="small"
            style={{ marginBottom: 2.5 }}
            onClick={() =>
              setDetailModal(orderAllList.find((item) => item.key === row.key))
            }
          >
            상세보기
          </Button>
        </>
      ),
    },
    // {
    //   title: '결제금액',
    //   key: 'shop_order',
    //   dataIndex: 'shop_order',
    //   align: 'center',
    //   width: 130,
    //   render: (shop_order) =>
    //     stringToMoneyFormat(shop_order?.final_price, '원'),
    // },
    // {
    //   title: '결제수단',
    //   key: 'payment_method',
    //   dataIndex: 'payment_method',
    //   align: 'center',
    //   width: 110,
    //   render: (val) => (
    //     <Tag color={PAYMENT_METHOD_CONSTANT[val]?.color}>
    //       {PAYMENT_METHOD_CONSTANT[val]?.title}
    //     </Tag>
    //   ),
    // },
    // {
    //   title: '주문상태',
    //   key: 'purchase_status',
    //   dataIndex: 'purchase_status',
    //   align: 'center',
    //   width: 130,
    //   render: (val) => (
    //     <Tag color={PURCHASE_STATUS[val]?.color}>
    //       {PURCHASE_STATUS[val]?.title}
    //     </Tag>
    //   ),
    // },
    // {
    //   title: '배송상태',
    //   key: 'delivery_status',
    //   dataIndex: 'delivery_status',
    //   align: 'center',
    //   width: 100,
    //   render: (val) => (
    //     <Tag color={DELIVERY_STATUS[val]?.color}>
    //       {DELIVERY_STATUS[val]?.title}
    //     </Tag>
    //   ),
    // },
    // {
    //   title: '주소/배송메모',
    //   key: 'shop_order',
    //   dataIndex: 'shop_order',
    //   width: 240,
    //   align: 'center',
    //   render: (record) => strCut(record?.delivery_address?.request, 18),
    // },
    // ...(type?.includes('RETURN') || type?.includes('EXCHANGE')
    //   ? [
    //       {
    //         title: '환불/반품 사유',
    //         key: 'refund',
    //         dataIndex: 'refund',
    //         width: 170,
    //         align: 'center',
    //         render: (item) => strCut(item?.[0]?.refund_reason, 18),
    //       },
    //     ]
    //   : []),
  ];
  const paymentListColumns = [
    {
      title: '택배사/송장번호',
      key: 'delivery_company',
      dataIndex: 'delivery_company',
      width: 145,
      align: 'center',
      fixed: 'left',
      render: (v, row) =>
        row?.parcel_service.type !== 'STORE_PICKUP' ? (
          row.invoice_number ? (
            <>
              <div>
                <span
                  style={{
                    fontSize: '0.88em',
                    letterSpacing: -0.8,
                  }}
                >{`${v ?? ''} - ${row.invoice_number ?? ''}`}</span>
              </div>
              <Button
                size="small"
                type="default"
                onClick={() =>
                  setRegisterDeliveryInfoModal({
                    delivery_address:
                      row.parcel_service?.shop_order?.delivery_address,
                    invoice_number: row.invoice_number,
                    delivery_company: row.delivery_company,
                    parcel_service_id: row.parcel_service_id,
                    regular_payment_id: row.regular_payment_id,
                  })
                }
              >
                <FormOutlined style={{ fontSize: '0.95em' }} /> 수정
              </Button>
            </>
          ) : (
            <>
              <Button
                type="primary"
                onClick={() =>
                  setRegisterDeliveryInfoModal({
                    delivery_address:
                      row?.parcel_service?.shop_order?.delivery_address,
                    invoice_number: row.invoice_number,
                    delivery_company: row.delivery_company,
                    parcel_service_id: row.parcel_service_id,
                    regular_payment_id: row.regular_payment_id,
                  })
                }
              >
                택배등록
              </Button>
            </>
          )
        ) : (
          <Button
            type="primary"
            // onClick={() => setRegisterDeliveryInfoModal(row)}
          >
            스토어픽업
          </Button>
        ),
    },
    {
      title: '결제일',
      key: 'payment_date',
      dataIndex: 'payment_date',
      width: 160,
      align: 'center',
      render: (payment_date) =>
        moment(payment_date * 1000).format('YYYY-MM-DD(HH:mm:ss)'),
      sorter: (a, b) => a.payment_date - b.payment_date,
    },

    {
      title: '주문번호',
      key: 'parcel_service',
      dataIndex: 'parcel_service',
      align: 'center',
      width: 100,
      render: (parcel_service) => parcel_service.shop_order_id,
      sorter: (a, b) =>
        a.parcel_service?.shop_order_id - b.parcel_service?.shop_order_id,
    },
    {
      title: '품목별주문번호',
      key: 'parcel_service_id',
      dataIndex: 'parcel_service_id',
      align: 'center',
      width: 120,
      render: (parcel_service_id) => parcel_service_id,
      sorter: (a, b) => a.parcel_service_id - b.parcel_service_id,
    },
    {
      title: '회차',
      key: 'regular_payment_id',
      dataIndex: 'regular_payment_id',
      width: 110,
      align: 'center',
      render: (regular_payment_id) => `${regular_payment_id} 회차`,
      sorter: (a, b) => a.regular_payment_id - b.regular_payment_id,
    },
    {
      title: '상품명',
      key: 'parcel_service',
      dataIndex: 'parcel_service',
      width: 180,
      align: 'center',
      ...getColumnSearchProps('parcel_service.product.product_name'),
      render: (parcel_service) => parcel_service.product.product_name,
    },
    {
      title: '옵션정보',
      key: 'parcel_service',
      dataIndex: 'parcel_service',
      width: 180,
      align: 'center',
      render: (parcel_service) => (
        <ul style={{ fontSize: '0.9em' }}>
          {parcel_service?.product?.product_option?.length
            ? parcel_service?.product?.product_option?.map((item) => (
                <li>
                  - {item.option_title} /{' '}
                  {item?.option?.map((option) => (
                    <>{option?.option_name || '-'}</>
                  ))}
                </li>
              ))
            : '-'}
        </ul>
      ),
    },

    {
      title: '판매자',
      key: 'parcel_service',
      dataIndex: 'parcel_service',
      width: 170,
      align: 'center',
      ...getColumnSearchProps(
        'parcel_service.shop_information.shop_information_name'
      ),
      render: (parcel_service) =>
        parcel_service?.shop_information?.shop_information_name ?? '',
    },
    {
      title: '구매자',
      key: 'parcel_service',
      dataIndex: 'parcel_service',
      width: 110,
      align: 'center',
      ...getColumnSearchProps('parcel_service.user.user_name'),
      render: (parcel_service) => parcel_service?.user?.user_name ?? '',
    },
    {
      title: '결제유형',
      dataIndex: 'payment_method',
      width: 100,
      render: (payment_method) => (
        <Tag color={PAYMENT_METHOD_CONSTANT[payment_method]?.color}>
          {PAYMENT_METHOD_CONSTANT[payment_method]?.title}
        </Tag>
      ),
      filters: Object.entries(PAYMENT_METHOD_CONSTANT).map(([key, v]) => ({
        text: v.title,
        value: key,
      })),
      onFilter: (val, record) => record.payment_method === val,
    },
    {
      title: '결제상태',
      dataIndex: 'payment_state',
      width: 100,
      render: (payment_state) => (
        <Tag color={PAYMENT_STATUS_CONSTANT[payment_state]?.color}>
          {PAYMENT_STATUS_CONSTANT[payment_state]?.title}
        </Tag>
      ),
      filters: Object.entries(PAYMENT_STATUS_CONSTANT).map(([key, v]) => ({
        text: v.title,
        value: key,
      })),
      onFilter: (val, record) => record.payment_state === val,
    },
    {
      title: '단가',
      key: 'product_price',
      dataIndex: 'product_price',
      align: 'center',
      width: 120,
      render: (_, record) => {
        const product = record?.parcel_service?.product;
        return stringToMoneyFormat(
          Number(product.price * product.num ?? 0),
          '원'
        );
      },
      sorter: (a, b) => {
        const productA = a?.parcel_service?.product;
        const productB = b?.parcel_service?.product;
        return productA.price * productA.num - productB.price * productB.num;
      },
    },
    {
      title: '수량',
      dataIndex: 'num',
      align: 'center',
      width: 120,
      render: (num) => stringToMoneyFormat(Number(num), '개'),
      sorter: (a, b) => a.num - b.num,
    },
    {
      title: '옵션금액',
      key: 'option_price',
      dataIndex: 'option_price',
      align: 'center',
      width: 120,
      render: (_, record) => {
        const product_option = record?.parcel_service?.product?.product_option;
        return stringToMoneyFormat(
          product_option?.reduce(
            (acc, cur) =>
              acc +
              cur?.option?.reduce((acc, cur) => acc + cur?.option_price, 0),
            0
          ),
          '원'
        );
      },
      sorter: (a, b) => {
        const product_optionA = a?.parcel_service?.product?.product_option;
        const product_optionB = b?.parcel_service?.product?.product_option;
        return (
          product_optionA?.reduce(
            (acc, cur) =>
              acc +
              cur?.option?.reduce((acc, cur) => acc + cur?.option_price, 0),
            0
          ) ||
          0 -
            product_optionB?.reduce(
              (acc, cur) =>
                acc +
                cur?.option?.reduce((acc, cur) => acc + cur?.option_price, 0),
              0
            ) ||
          0
        );
      },
    },
    {
      title: '상품합계금액',
      dataIndex: 'total_price',
      align: 'center',
      width: 140,
      render: (_, record) => {
        const product = record?.parcel_service?.product;
        return stringToMoneyFormat(
          Number(product.price * product.num) +
            (product?.product_option?.reduce(
              (acc, cur) =>
                acc +
                cur?.option?.reduce((acc, cur) => acc + cur?.option_price, 0),
              0
            ) || 0),
          '원'
        );
      },
      sorter: (a, b) => {
        const productA = a?.parcel_service?.product;
        const productB = b?.parcel_service?.product;
        return (
          productA.price * productA.num +
          (productA?.product_optionA?.reduce(
            (acc, cur) =>
              acc +
              cur?.option?.reduce((acc, cur) => acc + cur?.option_price, 0),
            0
          ) || 0) -
          productB.price * productB.num -
          (productB?.product_optionB?.reduce(
            (acc, cur) =>
              acc +
              cur?.option?.reduce((acc, cur) => acc + cur?.option_price, 0),
            0
          ) || 0)
        );
      },
    },
    {
      title: '배송비',
      key: 'delivery_price',
      dataIndex: 'delivery_price',
      align: 'center',
      width: 120,
      render: (v, record) => {
        if (record.regular_payment_id === 1) {
          return stringToMoneyFormat(
            Number(record.parcel_service?.shop_order?.final_delivery_price),
            '원(주문장)'
          );
        }
        return stringToMoneyFormat(Number(v), '원(상품당)');
      },
      sorter: (a, b) => a.shop_order_id - b.shop_order_id,
    },

    {
      title: '할인금액',
      dataIndex: 'discount_price',
      align: 'center',
      width: 140,
      render: (v) => stringToMoneyFormat(Number(v), '원'),
      sorter: (a, b) => a.discount_price - b.discount_price,
    },
    {
      title: '사용포인트',
      dataIndex: 'use_point',
      align: 'center',
      width: 120,
      render: (_, record) => {
        if (record.regular_payment_id === 1) {
          return stringToMoneyFormat(
            Number(record?.parcel_service?.product?.use_point),
            'P'
          );
        }
        return '0P';
      },
      sorter: (a, b) => {
        const productA = a?.parcel_service?.product;
        const productB = b?.parcel_service?.product;
        return productA.use_point || 0 - productB.use_point || 0;
      },
    },
    {
      title: '상품당 결제금액',
      dataIndex: 'price',
      align: 'center',
      width: 140,
      render: (v) => stringToMoneyFormat(Number(v), '원'),
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: '주문상태',
      key: 'purchase_status',
      dataIndex: 'purchase_status',
      align: 'center',
      width: 100,
      render: (val) => (
        <Tag color={PURCHASE_STATUS[val]?.color}>
          {PURCHASE_STATUS[val]?.title}
        </Tag>
      ),
      filters: Object.entries(PURCHASE_STATUS).map(([key, v]) => ({
        text: v.title,
        value: key,
      })),
      onFilter: (val, record) => record.purchase_status === val,
    },
    {
      title: '배송상태',
      key: 'delivery_status',
      dataIndex: 'delivery_status',
      align: 'center',
      width: 100,
      render: (val) => (
        <Tag color={DELIVERY_STATUS[val]?.color}>
          {DELIVERY_STATUS[val]?.title}
        </Tag>
      ),
      filters: Object.entries(DELIVERY_STATUS).map(([key, v]) => ({
        text: v.title,
        value: key,
      })),
      onFilter: (val, record) => record.delivery_status === val,
    },

    {
      title: '배송메모',
      key: 'parcel_service',
      dataIndex: 'parcel_service',
      align: 'center',
      width: 300,
      render: (parcel_service) =>
        parcel_service?.shop_order?.delivery_address?.request || '-',
    },
    {
      title: '#',
      align: 'center',
      width: 70,
      fixed: 'right',
      render: (row) => (
        <>
          <Button
            block
            size="small"
            style={{ marginBottom: 2.5 }}
            onClick={() => setDetailModal(row.parcel_service)}
          >
            상세보기
          </Button>
          <Button
            block
            size="small"
            disabled={!row.invoice_number ? true : false}
            onClick={() =>
              trackDelivery(row?.delivery_company, row?.invoice_number)
            }
          >
            배송조회
          </Button>
        </>
      ),
    },
  ];
  const expandableColumns = (parcel) => [
    {
      title: '택배사/송장번호',
      key: 'delivery_company',
      dataIndex: 'delivery_company',
      width: 145,
      align: 'center',
      fixed: 'left',
      render: (v, row) =>
        row?.type !== 'STORE_PICKUP' ? (
          row.invoice_number ? (
            <>
              <div>
                <span
                  style={{
                    fontSize: '0.88em',
                    letterSpacing: -0.8,
                  }}
                >{`${v ?? ''} - ${row.invoice_number ?? ''}`}</span>
              </div>
              <Button
                size="small"
                type="default"
                onClick={() =>
                  setRegisterDeliveryInfoModal({
                    delivery_address: parcel?.shop_order?.delivery_address,
                    invoice_number: row.invoice_number,
                    delivery_company: row.delivery_company,
                    parcel_service_id: parcel.parcel_service_id,
                    regular_payment_id: row.regular_payment_id,
                  })
                }
              >
                <FormOutlined style={{ fontSize: '0.95em' }} /> 수정
              </Button>
            </>
          ) : (
            <>
              <Button
                type="primary"
                onClick={() =>
                  setRegisterDeliveryInfoModal({
                    delivery_address: parcel?.shop_order?.delivery_address,
                    invoice_number: row.invoice_number,
                    delivery_company: row.delivery_company,
                    parcel_service_id: parcel.parcel_service_id,
                    regular_payment_id: row.regular_payment_id,
                  })
                }
              >
                택배등록
              </Button>
            </>
          )
        ) : (
          <Button
            type="primary"
            // onClick={() => setRegisterDeliveryInfoModal(row)}
          >
            스토어픽업
          </Button>
        ),
    },
    {
      title: '회차',
      dataIndex: 'regular_payment_id',
      align: 'center',
      width: 80,
      render: (v) => `${stringToMoneyFormat(Number(v))}회차`,
      sorter: (a, b) => a.regular_payment_id - b.regular_payment_id,
    },
    {
      title: '결제일',
      dataIndex: 'payment_date',
      width: 120,
      render: (v) => (v ? moment(v * 1000).format('YYYY-MM-DD') : '-'),
      sorter: (a, b) => a.payment_date - b.payment_date,
    },
    {
      title: '결제유형',
      dataIndex: 'payment_method',
      width: 100,
      render: (val) => (
        <Tag color={PAYMENT_METHOD_CONSTANT[val]?.color}>
          {PAYMENT_METHOD_CONSTANT[val]?.title}
        </Tag>
      ),
      filters: Object.entries(PAYMENT_METHOD_CONSTANT).map(([key, v]) => ({
        text: v.title,
        value: key,
      })),
      onFilter: (val, record) => record.payment_method === val,
    },
    {
      title: '결제상태',
      dataIndex: 'payment_state',
      width: 100,
      render: (val) => (
        <Tag color={PAYMENT_STATUS_CONSTANT[val]?.color}>
          {PAYMENT_STATUS_CONSTANT[val]?.title}
        </Tag>
      ),
      filters: Object.entries(PAYMENT_STATUS_CONSTANT).map(([key, v]) => ({
        text: v.title,
        value: key,
      })),
      onFilter: (val, record) => record.payment_state === val,
    },

    {
      title: '단가',
      dataIndex: 'product_price',
      align: 'center',
      width: 120,
      render: (_) =>
        stringToMoneyFormat(
          Number(parcel.product.price * parcel.product.num),
          '원'
        ),
    },
    {
      title: '수량',
      dataIndex: 'num',
      align: 'center',
      width: 120,
      render: (num) => stringToMoneyFormat(Number(num), '개'),
    },
    {
      title: '옵션금액',
      dataIndex: 'option_price',
      align: 'center',
      width: 120,
      render: (_) =>
        stringToMoneyFormat(
          parcel?.product?.product_option?.reduce(
            (acc, cur) =>
              acc +
              cur?.option?.reduce((acc, cur) => acc + cur?.option_price, 0),
            0
          ),
          '원'
        ),
    },

    {
      title: '상품합계금액',
      dataIndex: 'total_price',
      align: 'center',
      width: 140,
      render: (_) =>
        stringToMoneyFormat(
          parcel?.product?.price * parcel?.product?.num +
            (parcel?.product?.product_option?.reduce(
              (acc, cur) =>
                acc +
                cur?.option?.reduce((acc, cur) => acc + cur?.option_price, 0),
              0
            ) || 0),
          '원'
        ),
    },
    {
      title: '배송비',
      dataIndex: 'delivery_price',
      align: 'center',
      width: 140,
      render: (v, record) => {
        if (record.regular_payment_id === 1) {
          return stringToMoneyFormat(
            Number(parcel?.shop_order?.final_delivery_price),
            '원(주문장)'
          );
        }
        return stringToMoneyFormat(Number(v), '원(상품당)');
      },
    },
    {
      title: '할인금액',
      dataIndex: 'discount_price',
      align: 'center',
      width: 120,
      render: (v) => stringToMoneyFormat(Number(v), '원'),
    },
    {
      title: '사용포인트',
      dataIndex: 'use_point',
      align: 'center',
      width: 120,
      render: (_, record) => {
        if (record.regular_payment_id === 1) {
          return stringToMoneyFormat(Number(parcel.product.use_point), 'P');
        }
        return '0P';
      },
    },
    {
      title: '상품당 결제금액',
      dataIndex: 'price',
      align: 'center',
      width: 140,
      render: (v) => stringToMoneyFormat(Number(v), '원'),
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: '주문상태',
      key: 'purchase_status',
      dataIndex: 'purchase_status',
      align: 'center',
      width: 100,
      render: (val) => (
        <Tag color={PURCHASE_STATUS[val]?.color}>
          {PURCHASE_STATUS[val]?.title}
        </Tag>
      ),
      filters: Object.entries(PURCHASE_STATUS).map(([key, v]) => ({
        text: v.title,
        value: key,
      })),
      onFilter: (val, record) => record.purchase_status === val,
    },
    {
      title: '배송상태',
      key: 'delivery_status',
      dataIndex: 'delivery_status',
      align: 'center',
      width: 100,
      render: (val) => (
        <Tag color={DELIVERY_STATUS[val]?.color}>
          {DELIVERY_STATUS[val]?.title}
        </Tag>
      ),
      filters: Object.entries(DELIVERY_STATUS).map(([key, v]) => ({
        text: v.title,
        value: key,
      })),
      onFilter: (val, record) => record.delivery_status === val,
    },

    {
      title: '배송메모',
      key: 'delivery_memo',
      dataIndex: 'delivery_memo',
      align: 'center',
      width: 300,
      render: (_) => parcel?.shop_order?.delivery_address?.request || '-',
    },
    {
      title: '#',
      align: 'center',
      width: 70,
      fixed: 'right',
      render: (row) => (
        <>
          <Button
            block
            size="small"
            disabled={!row.invoice_number ? true : false}
            onClick={() =>
              trackDelivery(row?.delivery_company, row?.invoice_number)
            }
          >
            배송조회
          </Button>
        </>
      ),
    },
  ];
  /* ===== FUNCTION ===== */
  /**
   * 운송장 택배 등록
   * --
   */
  const handleSaveDeliveryInfo = async () => {
    const { delivery_company, invoice_number } = registerDeliveryInfo;
    if (!delivery_company || !invoice_number) {
      return MessageAlert.warning('택배정보를 입력해주세요');
    }
    await onSaveDeliveryInfo(
      {
        parcel_service_id: registerDeliveryInfoModal.parcel_service_id,
        regular_payment_id: registerDeliveryInfoModal.regular_payment_id,
        ...registerDeliveryInfo,
      },
      () => {
        setRegisterDeliveryInfoModal(null);
        setRegisterDeliveryInfo(initialRegisterDeliveryInfo);
      }
    );
  };

  /**--------------------
   * 한진택배 운송장 - 엑셀 다운로드
   * --------------------
   */
  const handleExportDeliveryData = () => {
    setLoading(true);
    try {
      const xlsxData = orderList.map((item, idx) => {
        const { product, shop_order, num } = item;
        const { product_name } = product;
        const { delivery_address } = shop_order;
        return {
          ['받으시는 분']: delivery_address.recipient_name,
          ['받으시는 분 전화']: delivery_address.recipient_phone,
          [' ']: `    `,
          ['받는 분 핸드폰']: delivery_address.recipient_phone,
          ['  ']: `    `,
          ['받는 분 주소']: `(${delivery_address.recipient_postcode}) ${delivery_address.recipient_address}, ${delivery_address.recipient_address_detail}`,
          ['내품수량']: num,
          ['내품명']: product_name,
          ['   ']: `    `,
          ['    ']: `    `,
          ['     ']: `    `,
          ['특기사항']: delivery_address.request,
        };
      });

      const ws = XLSX.utils.json_to_sheet(xlsxData);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      setTimeout(() => {
        XLSX.writeFile(
          wb,
          `택배정보 내보내기_${moment().format('YYYYMMDDHHmmss')}.xlsx`
        );
        setLoading(false);
      }, 1200);
    } catch (e) {
      setLoading(false);
      console.log(e);
      MessageAlert.error('엑셀을 다운로드 할 수 없습니다.');
    }
  };

  /**--------------------
   * 데이터 엑셀 다운로드
   * --------------------
   */
  const handleExportOrderData = (data) => {
    if (
      isNull(data) ||
      !window.confirm(
        '데이터를 엑셀로 다운로드합니다.\n데이터가 많을 경우 다소 시간이 소요됩니다.'
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      const xlsxData = data.flatMap((item) => {
        const { delivery_address } = item.shop_order;
        const regularCycle = (type) => {
          if (type === 'cycle') {
            const [count, type] = item?.regular_cycle?.split('-') || ['-', 'D'];
            if (type === 'D') {
              return `${count}일 주기`;
            } else if (type === 'W') {
              return `${count}주 주기`;
            } else if (type === 'M') {
              return `${count}개월 주기`;
            }
          } else if (type === 'late') {
            return `${item?.regular_cycle?.split('-')?.[2] ?? 0} 회`;
          }
          return '-';
        };

        return item.regular_payment_info?.map((regular) => {
          return {
            ['주문일(시간)']: moment(regular.payment_date * 1000).format(
              'YYYY-MM-DD(HH:mm:ss)'
            ),
            ['주문번호']: item?.shop_order_id,
            ['품목별주문번호']: item?.parcel_service_id,
            ['진행회차']: `${item?.regular_payment_info?.length ?? 0}회차`,
            ['주기']: regularCycle('cycle'),
            ['택배사(송장번호)']: regular?.invoice_number
              ? `${regular?.delivery_company}(${regular?.invoice_number})`
              : '',
            ['받는분핸드폰']: delivery_address.recipient_phone,
            ['받는분총주소']: `${delivery_address.recipient_address}, ${delivery_address.recipient_address_detail}`,
            ['수량']: `${item?.product?.product_name ?? ''} x ${regular?.num}`,
            ['상품명']: item?.product?.product_name ?? '',
            ['옵션정보']: item?.product?.product_option
              ?.map((_item) => `${_item.option_name}`)
              .join('\n'),
            ['판매자']: item?.shop_information?.shop_information_name ?? '',
            ['구매자']: item?.user.user_name,
            ['결제금액']: stringToMoneyFormat(regular?.price),
            ['결제수단']:
              PAYMENT_METHOD_CONSTANT[regular?.payment_method].title,
            ['배송시작일']: dayjs
              .unix(item?.delivery_date)
              .format('YYYY-MM-DD'),
            ['다음결제일']: dayjs
              .unix(item?.regular_updated_at)
              .format('YYYY-MM-DD'),
            ['진행상태']: item?.is_active === 'Y' ? '진행중' : '종료',
            ['미루기여부']:
              item?.is_regular_postponed === 'N' ? '미루지않음' : '미루기',
            ['미루기횟수']: regularCycle('late'),
            ['최대미루기횟수']: item?.max_regular_delivery_count,
            ['주문상태']: PURCHASE_STATUS[regular?.purchase_status].title,
            ['배송상태']: DELIVERY_STATUS[regular?.delivery_status].title,
            ['주소/배송메모']: delivery_address.request,
          };
        });
      });

      const ws = XLSX.utils.json_to_sheet(xlsxData);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      setTimeout(() => {
        XLSX.writeFile(
          wb,
          `주문 데이터 내보내기_${moment().format('YYYYMMDD_HHmmss')}.xlsx`
        );
        setLoading(false);
      }, 1200);
    } catch (e) {
      setLoading(false);
      console.log(e);
      MessageAlert.error('엑셀을 다운로드 할 수 없습니다.');
    }
  };

  /**--------------------
   * 파일 업로드
   * --------------------
   */
  const handleFile = async (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const wsname = workbook.SheetNames[0];
      const ws = workbook.Sheets[wsname];
      const dataParse = XLSX.utils.sheet_to_json(ws);

      // 데이터 추출
      const result = dataParse.map((item) => {
        const newItem = {
          parcel_service_id: item['품목별주문번호'],
          regular_payment_id: item['회차'],
          invoice_number: item['운송장번호'],
          num: item['수량'],
          delivery_company: item['배송업체번호'],
        };
        return newItem;
      });
      setUploadData(result);
      MessageAlert.success(`파일이 업로드 되었습니다.`);
    };

    reader.onerror = (error) => {
      MessageAlert.error('파일을 읽는 중 오류가 발생했습니다.');
      console.error('File reading error:', error);
    };

    reader.readAsArrayBuffer(file); // 실제 파일 객체를 읽음
  };

  /**--------------------
   * 엑셀 양식 다운로드
   * --------------------
   */
  const handleGetExcelTemplate = (type, data) => {
    try {
      const xlsxData = data.map((_item) => {
        return {
          ['품목별주문번호']: '',
          ['회차']: '',
          ['운송장번호']: '',
          ['수량']: '',
          ['배송업체번호']: currentTab === 1 ? currentDeliveryCompany : null,
        };
      });

      const ws = XLSX.utils.json_to_sheet(xlsxData);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(
        wb,
        `운송장_일괄등록_양식_${moment().format('YYYYMMDD_HHmmss')}.xlsx`
      );
    } catch (e) {
      console.log(e);
      MessageAlert.error('엑셀을 다운로드 할 수 없습니다.');
    }
  };

  /**--------------------
   * 운송장 일괄저장=> 엑셀이 등록된 경우에만 처리가능
   * --------------------
   */
  const handleSaveInvoice = async () => {
    setLoading(true);
    try {
      /* 예외처리 */
      // 빈값체크
      if (!uploadData || uploadData.length < 1) {
        MessageAlert.warning('엑셀파일을 등록해주세요');
        setLoading(false);
        return;
      }
      // 양식체크
      if (
        !uploadData[0].parcel_service_id ||
        !uploadData[0].regular_payment_id ||
        !uploadData[0].invoice_number ||
        !uploadData[0].delivery_company
      ) {
        MessageAlert.warning('업로드된 파일의 양식을 확인해주세요');
        setLoading(false);
        return;
      }
      // 데이터 만들기
      const dc = Object.keys(DeliveryCompany);
      const newData = uploadData.map((item) => {
        const findKey = dc.find(
          (key) => DeliveryCompany[key].shippingCode === item.delivery_company
        );
        return {
          parcel_service_id: item.parcel_service_id,
          regular_payment_id: item.regular_payment_id,
          invoice_number: `${item.invoice_number}`,
          delivery_company: DeliveryCompany[findKey].value,
        };
      });
      const { status, message } = await updateParcelServiceStatusBulk(newData);

      if (status !== 200) throw { message };
      setTimeout(() => {
        MessageAlert.success('저장되었습니다');
        onReload();
        setLoading(false);
      }, 1000);
      // API
    } catch (e) {
      setLoading(false);
      console.log('[OrderData][handleSaveInvoice] Error: ', e);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 배송 상태 변경
   * --------------------
   */
  const handleDepositCheck = async (value) => {
    if (!selectedList?.length) {
      MessageAlert.warning('처리할 항목을 선택해주세요');
      return;
    }

    try {
      // // 입금상태가 PENDING인경우만 상태변경처리
      // let deliveryStatus;
      // if (type === 'PENDING') {
      //   deliveryStatus = 'PREPARING';
      // } else if (type === 'PREPARING') {
      //   deliveryStatus = 'SHIPPING';
      // } else if (type === 'SHIPPING') {
      //   deliveryStatus = 'DELIVERED';
      // }
      // const newData = selectedList.map((item) => ({
      //   parcel_service_id: item.parcel_service_id,
      //   delivery_status: 'PREPARING',
      // }));
      const newData = selectedList.map((item) => ({
        parcel_service_id: item.parcel_service_id,
        regular_payment_id: item.regular_payment_id,
        delivery_status: value,
        // [key]: value,
      }));
      const { status, message } = await updateParcelServiceStatusBulk(newData);
      if (status !== 200) throw { message };
      onReload();
      MessageAlert.info(
        `${DELIVERY_STATUS?.[value]?.title} 상태로 변경되었습니다.`
      );
    } catch (e) {
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 결제 미룩기
   * --------------------
   */
  const handleBulkUpdateParcelService = async (
    isModalData = true,
    value,
    type
  ) => {
    const updateValue = isModalData ? dateModal : value;
    if (!updateValue) {
      return MessageAlert.info('변경할 데이터를 선택해주세요');
    }
    const {
      parcel_service_id,
      is_active,
      regular_updated_at,
      dateValue,
      regular_cycle,
    } = updateValue;

    const values = {
      is_active: {
        message: '진행상태',
        data: {
          parcel_service_id,
          is_active,
        },
      },
      is_regular_postponed: {
        message: '미루기 처리',
        data: {},
      },
      regular_updated_at: {
        message: '결제예정일',
        data: {
          parcel_service_id,
          regular_updated_at: dateValue,
        },
      },
      delivery_date: {
        message: '배송시작일',
        data: {
          parcel_service_id,
          delivery_date: dateValue,
        },
      },
    };
    ////
    if (type === 'is_regular_postponed') {
      const [num, cycle, delay] = regular_cycle.split('-');
      values.is_regular_postponed.data = {
        parcel_service_id,
        is_regular_postponed: 'Y',
        regular_updated_at: getPaymentDueDate(
          regular_updated_at,
          regular_cycle
        ), // 결제 예정일 업데이트
        regular_cycle: `${num}-${cycle}-${Number(delay) + 1}`, // 미루기 횟수 업데이트
      };
    }

    if (!window.confirm(`${values[type].message}를 하시겠습니까?`)) return;
    try {
      console.log(values[type].data);
      const { status, message } = await bulkUpdateParcelService([
        values[type].data,
      ]);
      if (status !== 200) throw { message };
      onReload();
      setDateModal(null);
      MessageAlert.info(`${values[type].message}가 변굥되었습니다.`);
    } catch (e) {
      console.error('[handleRegularStatus] Error!! ', e);
      MessageAlert.error(`${values[type].message}에 실패하였습니다!`);
    }
  };

  /* ===== HOOKS ===== */
  /*  */
  useEffect(() => {
    registerDeliveryInfoModal &&
      setRegisterDeliveryInfo({
        delivery_company: registerDeliveryInfoModal.delivery_company,
        invoice_number: registerDeliveryInfoModal.invoice_number,
      });
  }, [registerDeliveryInfoModal]);

  useEffect(() => {
    if (detailModal) {
      setDetailModal(
        orderList?.find(
          (i) => i.parcel_service_id === detailModal.parcel_service_id
        )
      );
    }
  }, [orderList]);
  /* ===== RENDER ===== */
  // JSX: start
  return (
    <>
      <Spin spinning={loading}>
        <Content
          maxWidth={'100%'}
          width={1400}
          height={'100%'}
          minHeight={490}
          borderRadius={4}
          padding={20}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <Title size={5}>
              검색 결과 총 <span>{orderList?.length}</span>건
            </Title>
            <div>
              {/* 배송준비처리 */}
              {Object.values(DELIVERY_STATUS).map((item, index) => (
                <Button
                  size="default"
                  style={{ marginLeft: 5 }}
                  // type="primary"
                  onClick={() => handleDepositCheck(item.value)}
                >
                  {item.title} 처리
                </Button>
              ))}
              {/* {type !== 'DELIVERED' && (
                <Button
                  size="default"
                  style={{ marginLeft: 5 }}
                  type="primary"
                  disabled={selectedList?.length > 0 ? false : true}
                  onClick={handleDepositCheck}
                >
                  {type === 'PENDING'
                    ? '배송준비중'
                    : type === 'PREPARING'
                    ? '배송중'
                    : type === 'SHIPPING'
                    ? '배송완료'
                    : type === 'ON_HOLD'
                    ? '배송중'
                    : ''}{' '}
                  처리
                </Button>
              )} */}
              <Button
                size="default"
                style={{ marginLeft: 5 }}
                onClick={() => handleExportOrderData(orderList)}
              >
                데이터 엑셀 다운로드
              </Button>
              {type === 'PENDING' ||
                (type === 'PREPARING' && (
                  <>
                    <Button
                      size="default"
                      style={{ marginLeft: 5 }}
                      onClick={handleExportDeliveryData}
                    >
                      택배정보 내보내기
                    </Button>
                    {/* <Button
                      size="default"
                      style={{ marginLeft: 5 }}
                      // type="primary"
                      disabled={selectedList?.length > 0 ? false : true}
                      onClick={handleSaveInvoice}
                    >
                      운송장 일괄등록
                    </Button> */}
                  </>
                ))}
            </div>
          </div>
          <Flex gap={5} style={{ marginBottom: 10 }}>
            <Radio.Group
              value={tavType}
              type="button"
              onChange={(e) => setTabType(e.target.value)}
              style={{ flex: 1 }}
            >
              <Radio value="SUBSCRIPTION">신청기준으로 보기</Radio>
              <Radio value="PAYMENT">결제목록만 보기</Radio>
            </Radio.Group>
          </Flex>
          <div
            style={{
              overflowX: 'auto',
            }}
          >
            <div
              style={
                {
                  // width: 1650,
                }
              }
            >
              {/* 
            NOTE: 
            deliver => 일반 상품
            subscription && is_active : Y인것은 구독중인 상품
            */}
              {/* SUBSCRIPTION - PAYMENT */}
              {tavType === 'SUBSCRIPTION' ? (
                <Table
                  size="small"
                  // data={orderList}
                  dataSource={orderList.map((item) => ({
                    ...item,
                    key: item.parcel_service_id,
                  }))}
                  pagination={false}
                  bordered
                  // isChecked
                  // rowSelection={{
                  //   type: 'checkbox',
                  //   selectedRowKeys: selectedList?.map((item) => item.key),
                  //   onChange: (_selectedRowKeys, _selectedRows) => {
                  //     setSelectedList(_selectedRows);
                  //   },
                  // }}
                  scroll={{ x: 'max-content' }}
                  columns={columns}
                  // rental_updated_at => 다음결제 예정일
                  expandable={{
                    expandedRowRender: (currentRow) => {
                      return (
                        <Table
                          dataSource={
                            currentRow?.regular_payment_info
                              ?.map((item) => ({
                                ...item,
                                key: `${currentRow?.parcel_service_id}-${item.regular_payment_id}`,
                                parcel_service_id:
                                  currentRow?.parcel_service_id,
                              }))
                              .sort(
                                (a, b) =>
                                  b.regular_payment_id - a.regular_payment_id
                              ) ?? []
                          }
                          columns={expandableColumns(currentRow)}
                          selectedRowKeys={selectedRowKeys}
                          rowSelection={{
                            type: 'checkbox',
                            onSelect: (record, selected) => {
                              setSelectedList((prev) => {
                                const exists = prev.some(
                                  (item) => item.key === record.key
                                );
                                if (selected && !exists) {
                                  return [...prev, record];
                                } else if (!selected) {
                                  return prev.filter(
                                    (item) => item.key !== record.key
                                  );
                                }
                                return prev;
                              });

                              setSelectedRowKeys((prev) => {
                                if (selected) {
                                  return [...prev, record.key];
                                } else {
                                  return prev.filter(
                                    (key) => key !== record.key
                                  );
                                }
                              });
                            },

                            // 전체 선택/해제
                            onSelectAll: (
                              selected,
                              selectedRows,
                              changeRows
                            ) => {
                              const changeKeys = changeRows.map(
                                (row) => row.key
                              );

                              if (selected) {
                                setSelectedList((prev) => {
                                  const existingKeys = new Set(
                                    prev.map((item) => item.key)
                                  );
                                  const newItems = changeRows.filter(
                                    (row) => !existingKeys.has(row.key)
                                  );
                                  return [...prev, ...newItems];
                                });

                                setSelectedRowKeys((prev) => {
                                  const existingSet = new Set(prev);
                                  const newKeys = changeKeys.filter(
                                    (key) => !existingSet.has(key)
                                  );
                                  return [...prev, ...newKeys];
                                });
                              } else {
                                setSelectedList((prev) =>
                                  prev.filter(
                                    (item) => !changeKeys.includes(item.key)
                                  )
                                );
                                setSelectedRowKeys((prev) =>
                                  prev.filter(
                                    (key) => !changeKeys.includes(key)
                                  )
                                );
                              }
                            },
                          }}
                          scroll={{ x: 'max-content' }}
                        />
                      );
                      // selectedRowKeys: currentRow[parentId] || [],
                    },

                    // defaultExpandedRowKeys: ['1', '2', '3'],
                    // expandedRowKeys: newData3,
                  }}
                />
              ) : (
                <Table
                  size="small"
                  scroll={{ x: 'max-content' }}
                  dataSource={
                    orderList
                      ?.flatMap((item) => {
                        return item?.regular_payment_info?.map((regular) => ({
                          ...regular,
                          key: `${item?.parcel_service_id}-${regular.regular_payment_id}`,
                          parcel_service_id: item?.parcel_service_id,
                          parcel_service: item,
                        }));
                      })
                      ?.sort((a, b) => b.payment_date - a.payment_date) ?? []
                  }
                  columns={paymentListColumns}
                  selectedRowKeys={selectedRowKeys}
                  rowSelection={{
                    type: 'checkbox',
                    onSelect: (record, selected) => {
                      setSelectedList((prev) => {
                        const exists = prev.some(
                          (item) => item.key === record.key
                        );
                        if (selected && !exists) {
                          return [...prev, record];
                        } else if (!selected) {
                          return prev.filter((item) => item.key !== record.key);
                        }
                        return prev;
                      });

                      setSelectedRowKeys((prev) => {
                        if (selected) {
                          return [...prev, record.key];
                        } else {
                          return prev.filter((key) => key !== record.key);
                        }
                      });
                    },

                    // 전체 선택/해제
                    onSelectAll: (selected, selectedRows, changeRows) => {
                      const changeKeys = changeRows.map((row) => row.key);

                      if (selected) {
                        setSelectedList((prev) => {
                          const existingKeys = new Set(
                            prev.map((item) => item.key)
                          );
                          const newItems = changeRows.filter(
                            (row) => !existingKeys.has(row.key)
                          );
                          return [...prev, ...newItems];
                        });

                        setSelectedRowKeys((prev) => {
                          const existingSet = new Set(prev);
                          const newKeys = changeKeys.filter(
                            (key) => !existingSet.has(key)
                          );
                          return [...prev, ...newKeys];
                        });
                      } else {
                        setSelectedList((prev) =>
                          prev.filter((item) => !changeKeys.includes(item.key))
                        );
                        setSelectedRowKeys((prev) =>
                          prev.filter((key) => !changeKeys.includes(key))
                        );
                      }
                    },
                  }}
                />
              )}
            </div>
          </div>
          {/* <Pagination
            total={orderList.length}
            //total={users?.length}
            align="center"
            style={{
              marginTop: 40,
              display: 'flex',
              justifyContent: 'center',
            }}
          /> */}
        </Content>

        {/* ===== 운송장번호 일괄처리 ===== */}
        {type !== 'PENDING' ? (
          <Content
            maxWidth={'100%'}
            width={1400}
            height={'100%'}
            minHeight={490}
            borderRadius={4}
            padding={20}
            style={{ marginTop: 25 }}
          >
            <Flex align="center" justify="space-between">
              <Title>엑셀 일괄배송 처리</Title>
              <Button
                size="default"
                style={{ marginLeft: 5 }}
                type="primary"
                // disabled={selectedList?.length > 0 ? false : true}
                onClick={handleSaveInvoice}
              >
                운송장 일괄등록
              </Button>
            </Flex>

            <Tabs
              defaultActiveKey="1"
              items={[
                // === 단일 배송사 === //
                {
                  key: 1,
                  label: '엑셀 하나에 배송사 1개',
                },

                // === 여러 배송사 === //
                {
                  key: 2,
                  label: '엑셀 하나에 배송사 여러개',
                },
              ]}
              onChange={(key) => {
                setCurrentTab(key);
              }}
            />
            <Descriptions
              column={2}
              // size="small"
              bordered
              labelStyle={{
                width: 125,
                color: '#111',
                fontWeight: '500',
                background: '#f4f4f4',
              }}
              style={{
                borderTop: '2px solid #d1d1d1',
                borderRadius: 5,
                marginTop: 5,
              }}
              items={[
                /* === Line 1 === */
                ...(currentTab === 1
                  ? [
                      {
                        key: 'shop_order_id',
                        label: '배송사',
                        span: 2,
                        children: (
                          <Select
                            style={{ width: 200 }}
                            value={currentDeliveryCompany}
                            onChange={(value) =>
                              setCurrentDeliveryCompany(value)
                            }
                          >
                            {deliveryCompanyList.map((item) => (
                              <Select.Option
                                key={`dcl_${item}`}
                                value={DeliveryCompany[item].shippingCode}
                              >
                                {DeliveryCompany[item].label}:{' '}
                                {DeliveryCompany[item].shippingCode}
                              </Select.Option>
                            ))}
                          </Select>
                        ),
                      },
                    ]
                  : []),

                /* === Line 2 === */
                {
                  key: 'shop_order_id',
                  label: '엑셀파일등록',
                  // span: 2,
                  children: (
                    <Upload
                      // onChange={handleFile}
                      onRemove={(_file) => setUploadData(null)}
                      beforeUpload={(file) => {
                        handleFile(file);
                        return false;
                      }}
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>
                        엑셀파일을 양식에 맞추어 업로드해주세요
                      </Button>
                    </Upload>
                  ),
                },
                {
                  key: 'payment_serial_number',
                  label: '양식다운로드',
                  width: 200,
                  children: (
                    <Button
                      onClick={() =>
                        handleGetExcelTemplate('single', [1, 2, 3, 4, 5])
                      }
                    >
                      엑셀 양식 다운로드
                    </Button>
                  ),
                },
                /* === Line 3 === */
                {
                  key: 'shop_order_id',
                  label: '처리결과',
                  span: 2,
                  children: (
                    <div
                      style={{
                        width: '100%',
                        background: '#fafafa',
                        border: '1px solid #f1f1f1',
                        padding: 5,
                        height: 120,
                        overflowX: 'auto',
                      }}
                    >
                      <ul>
                        {uploadData && uploadData.length > 0 ? (
                          uploadData.map((item, key) => (
                            <li
                              key={`asdiwm_${key}`}
                              style={{
                                borderBottom: '1px solid #f1f1f1',
                                color: '#5a5a5a',
                                fontWeight: '300',
                              }}
                            >
                              {JSON.stringify(item)}
                            </li>
                          ))
                        ) : (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                      </ul>
                    </div>
                  ),
                },
              ]}
            />
          </Content>
        ) : null}
      </Spin>

      {/* ===== 택배정보 등록 모달 start ===== */}
      <ModalLayout
        title="택배사/송장번호 등록"
        width={530}
        closable={true}
        open={registerDeliveryInfoModal !== null}
        onCancel={() => setRegisterDeliveryInfoModal(null)}
        footer={
          <>
            <Button
              type="primary"
              size="large"
              onClick={() =>
                handleSaveDeliveryInfo(
                  registerDeliveryInfoModal,
                  setRegisterDeliveryInfoModal(null)
                )
              }
            >
              등록
            </Button>
          </>
        }
      >
        <Divider style={{ margin: '15px 0' }} />
        {registerDeliveryInfoModal && (
          <>
            {/* === 택배사/송장번호 입력폼 start === */}
            <div>
              <Space.Compact size="large" style={{ width: '100%' }}>
                <Select
                  style={{ width: 170 }}
                  placeholder="택배사"
                  options={Object.keys(DeliveryCompany).map((item) => ({
                    value: DeliveryCompany[item].value,
                    label: DeliveryCompany[item].label,
                  }))}
                  value={registerDeliveryInfo?.delivery_company}
                  onChange={(e) =>
                    setRegisterDeliveryInfo((p) => ({
                      ...p,
                      delivery_company: e,
                    }))
                  }
                />
                <Input
                  placeholder="송장번호를 입력해주세요"
                  style={{ width: 'calc(100% - 170px)' }}
                  value={registerDeliveryInfo?.invoice_number}
                  onChange={(e) =>
                    setRegisterDeliveryInfo((p) => ({
                      ...p,
                      invoice_number: e.target.value,
                    }))
                  }
                />
              </Space.Compact>
            </div>
            {/* === 택배사/송장번호 입력폼 end === */}

            <Divider style={{ margin: '15px 0' }} />

            {/* === 확인정보 start === */}
            <Title size={5}>입력정보 확인</Title>
            <div style={{ padding: 3.5, lineHeight: 1.85 }}>
              {(() => {
                /* Variables */
                const {
                  recipient_postcode,
                  recipient_address,
                  recipient_address_detail,
                } = registerDeliveryInfoModal.delivery_address;
                const { delivery_company, invoice_number } =
                  registerDeliveryInfo;

                /* Render */
                return (
                  <p>
                    주소:
                    {` [${recipient_postcode}] ${recipient_address}(${recipient_address_detail})`}
                    <br />
                    택배사(코드): {DeliveryCompany[delivery_company]?.label}(
                    {DeliveryCompany[delivery_company]?.value})
                    <br />
                    송장번호: {invoice_number}
                  </p>
                );
              })()}
            </div>
            {/* === 확인정보 end === */}
          </>
        )}
        <Divider style={{ margin: '15px 0' }} />
      </ModalLayout>
      {/* ===== 택배정보 등록 모달: end ===== */}
      {/* ===== 상세모달: start ===== */}
      <ModalLayout
        type="drawer"
        placement="right"
        title="주문 상세보기"
        width={1350}
        maxWidth={1350}
        closable={true}
        open={detailModal !== null}
        onCancel={() => setDetailModal(null)}
      >
        {detailModal && (
          <RegularDetail
            // orderId={detailModal.shop_order_id}
            orderDetailData={detailModal}
            onCancel={onCancel}
            onReload={onReload}
            // type={type}
            // modalType={'shipping'}
            // parcelId={detailModal.parcel_service_id}
          />
        )}
      </ModalLayout>

      <ModalLayout
        title={dateModal?.title}
        width={530}
        closable={true}
        open={dateModal !== null}
        onCancel={() => setDateModal(null)}
        footer={
          <>
            <Button
              type="primary"
              size="large"
              onClick={() =>
                handleBulkUpdateParcelService(true, null, dateModal.key)
              }
            >
              수정
            </Button>
          </>
        }
      >
        <DatePicker
          style={{ width: '100%' }}
          value={dayjs.unix(dateModal?.dateValue)}
          onChange={(e) => {
            setDateModal((p) => ({
              ...p,
              dateValue: dayjs(e).unix(),
            }));
          }}
          format={'YYYY-MM-DD HH:mm:ss'}
          showTime
        />
      </ModalLayout>

      {/* ===== 상세모달: end ===== */}
    </>
  ); // JSX: end
};

RegularData.defaultProps = {
  type: 'SHIPPING',
  orderList: [],
  onSaveDeliveryInfo: () => {},
  onReload: () => {},
};
export default RegularData;
